import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text, TouchableRipple } from 'react-native-paper';
import Svg, { Path } from 'react-native-svg';
import Animated, {
  cancelAnimation,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

import { formatDuration } from '@/formatters/formatDuration';
import { type RemainingRecordingSeverity } from '@/models/recordingDuration';
import { iconSize, rippleOnSurface } from '@/theme';

import { remainingSeverityStyles, TEXT_ND_SECONDARY } from './remainingSeverityStyles';

const RECORDING_RED = '#F44336';
const STOP_RED = '#f03e3e';
const ICON_ND_PRIMARY = '#335267';
const TEXT_ND_PRIMARY = '#17344a';

interface RecordingBarProps {
  readonly statusLabel: string;
  readonly elapsedSeconds: number;
  readonly isPaused: boolean;
  readonly remainingLabel?: string;
  readonly remainingSeverity?: RemainingRecordingSeverity;
  readonly disabled?: boolean;
  readonly labels: {
    readonly expand: string;
    readonly playPause: string;
    readonly stop: string;
  };
  readonly onAction: () => void;
  readonly onStop: () => void;
  readonly onExpand: () => void;
}

interface ActionButtonProps {
  readonly iconName: keyof typeof MaterialCommunityIcons.glyphMap;
  readonly accessibilityLabel: string;
  readonly disabled?: boolean;
  readonly onPress: () => void;
}

const ActionButton = ({ iconName, accessibilityLabel, disabled, onPress }: ActionButtonProps) => (
  <TouchableRipple
    accessibilityRole="button"
    accessibilityLabel={accessibilityLabel}
    disabled={disabled}
    onPress={onPress}
    rippleColor={rippleOnSurface}
    style={styles.actionButton}>
    <MaterialCommunityIcons color={ICON_ND_PRIMARY} name={iconName} size={iconSize.md} />
  </TouchableRipple>
);

export const RecordingBar = ({
  statusLabel,
  elapsedSeconds,
  isPaused,
  remainingLabel,
  remainingSeverity = 'normal',
  disabled = false,
  labels,
  onAction,
  onStop,
  onExpand,
}: RecordingBarProps) => {
  const opacity = useSharedValue(1);

  useEffect(() => {
    if (isPaused) {
      cancelAnimation(opacity);
      opacity.value = withTiming(1, { duration: 200 });

      return;
    }

    opacity.value = withRepeat(
      withSequence(withTiming(0.4, { duration: 800 }), withTiming(1, { duration: 800 })),
      -1,
      true,
    );

    return () => cancelAnimation(opacity);
  }, [isPaused, opacity]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  const actionIcon = isPaused ? 'play' : 'pause';

  return (
    <View testID="recording-bar" style={styles.container}>
      <View style={styles.contentSection}>
        <Animated.View style={animatedStyle}>
          <MaterialCommunityIcons color={RECORDING_RED} name="radiobox-marked" size={iconSize.md} />
        </Animated.View>
        <View style={styles.textContainer}>
          <Text style={styles.statusText}>{statusLabel}</Text>
          <View style={styles.timerRow}>
            <Text style={styles.timerText}>{formatDuration(elapsedSeconds * 1000)}</Text>
            {remainingLabel ? (
              <>
                <Text style={styles.remainingSeparator}>・</Text>
                <Text
                  testID="recording-bar-remaining-time"
                  style={[styles.remainingText, remainingSeverityStyles[remainingSeverity]]}>
                  {remainingLabel}
                </Text>
              </>
            ) : null}
          </View>
        </View>
      </View>
      <View style={styles.actionsContainer}>
        <TouchableRipple
          accessibilityRole="button"
          accessibilityLabel={labels.expand}
          onPress={onExpand}
          rippleColor={rippleOnSurface}
          style={styles.actionButton}>
          <Svg fill="none" height={24} viewBox="0 0 24 24" width={24}>
            <Path d="M5 19V13H7V17H11V19H5ZM17 11V7H13V5H19V11H17Z" fill={ICON_ND_PRIMARY} />
          </Svg>
        </TouchableRipple>
        <ActionButton
          accessibilityLabel={labels.playPause}
          disabled={disabled}
          iconName={actionIcon}
          onPress={onAction}
        />
        <TouchableRipple
          accessibilityRole="button"
          accessibilityLabel={labels.stop}
          disabled={disabled}
          onPress={onStop}
          rippleColor={rippleOnSurface}
          style={styles.actionButton}>
          <View style={styles.stopCircle}>
            <View style={styles.stopSquare} />
          </View>
        </TouchableRipple>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: 72,
    backgroundColor: '#FFFFFF',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    shadowColor: '#254157',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.7,
    shadowRadius: 5,
    elevation: 5,
  },
  contentSection: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  textContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  statusText: {
    color: TEXT_ND_SECONDARY,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '500',
  },
  timerText: {
    color: TEXT_ND_PRIMARY,
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '700',
  },
  timerRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 8,
  },
  remainingSeparator: {
    color: TEXT_ND_SECONDARY,
    fontSize: 12,
    lineHeight: 16,
  },
  remainingText: {
    color: TEXT_ND_SECONDARY,
    fontSize: 12,
    lineHeight: 16,
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  actionButton: {
    alignItems: 'center',
    borderRadius: 20,
    height: 40,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 40,
  },
  stopCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: STOP_RED,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stopSquare: {
    width: 10,
    height: 10,
    borderRadius: 2,
    backgroundColor: '#FFFFFF',
  },
});
